import React from 'react';
import KeystoneLogo, { KeystoneMark } from '../components/KeystoneLogo';
import { downloadServerPDF } from '../lib/pdf-download';

/**
 * Keystone by CloudVoro, Conversation Brochure (5 pages).
 *
 * Purpose: the SHORT asset. Optimised for a single outcome: a growing
 *   food manufacturer reads this and thinks "this sounds exactly like us",
 *   then replies for a 20-minute conversation. Deliberately not
 *   comprehensive — the 16-page detailed brochure lives at
 *   /brochure/buyer-pack and is the follow-up after interest exists.
 *
 * Structure (per commercial editorial guidance, Feb 2026):
 *   Page 1  The belief.
 *   Page 2  Their reality (paper factory bolted onto a food factory).
 *   Page 3  The operational spine (inwards / production / outwards / trace).
 *   Page 4  Proof (built with Cashel Blue, not demonstrated to them).
 *   Page 5  The conversation ("bring us your worst Friday").
 *
 * Voice: European SME, understated, no SaaS cliches, no em-dashes,
 *   no fabricated pull-quotes. Where a real person is named, only their
 *   role and public facts are used until on-record testimonials arrive.
 *
 * Public route. Print-perfect A4. Server-rendered to PDF via
 *   /api/marketing/render-pdf (path whitelisted in
 *   backend/routes/marketing.py).
 */

const TOTAL_PAGES = 5;

const C = {
  navy: '#0A2540',
  navyDeep: '#061A2E',
  magenta: '#E91E63',
  gold: '#C9A961',
  cream: '#FAF8F5',
  paper: '#FFFFFF',
  ink: '#0F1B2A',
  slate: '#3D4A5C',
  muted: '#6B7280',
  border: '#E2E8F0',
  green: '#166534',
};

// ---------- Page primitives (shared visual language with buyer pack) ----------

const Page = ({ children, bg = '#fff', last = false, pageNum, noPad = false }) => (
  <section
    className="brochure-page"
    data-page={pageNum}
    style={{
      width: '210mm',
      height: '297mm',
      background: bg,
      position: 'relative',
      overflow: 'hidden',
      pageBreakAfter: last ? 'auto' : 'always',
      breakAfter: last ? 'auto' : 'page',
      boxShadow: '0 8px 32px rgba(10, 37, 64, 0.12)',
      margin: '0 auto 24px',
      padding: noPad ? 0 : '14mm 16mm',
      boxSizing: 'border-box',
      fontFamily: "'Inter', 'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif",
      color: C.ink,
    }}
  >
    {children}
  </section>
);

const PageHeader = ({ pageNum, dark = false }) => (
  <div
    style={{
      position: 'absolute',
      top: '8mm', left: '16mm', right: '16mm',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      fontSize: '8.5pt',
      color: dark ? 'rgba(255,255,255,0.65)' : C.muted,
      letterSpacing: '0.08em',
      textTransform: 'uppercase',
      fontWeight: 600,
    }}
  >
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <KeystoneMark size={20} variant={dark ? 'dark' : 'light'} />
      <span style={{ color: dark ? '#fff' : C.navy, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'none', fontSize: '9.5pt' }}>Keystone</span>
      <span style={{ fontSize: '7.5pt' }}>·</span>
      <span style={{ color: dark ? C.gold : C.magenta, fontSize: '7.5pt', letterSpacing: '0.15em' }}>BY CLOUDVORO</span>
    </div>
    <span>Conversation brochure · {pageNum} / {TOTAL_PAGES}</span>
  </div>
);

const PageFooter = ({ dark = false }) => (
  <div
    style={{
      position: 'absolute',
      bottom: '8mm', left: '16mm', right: '16mm',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      fontSize: '7.5pt',
      color: dark ? 'rgba(255,255,255,0.55)' : C.muted,
      letterSpacing: '0.06em',
    }}
  >
    <span>keystone.cloudvoro.com</span>
    <span>Cloudvoro Ltd &middot; Thurles, Co. Tipperary, Ireland</span>
  </div>
);

const H1 = ({ children, style }) => (
  <h1 style={{
    fontFamily: "'Playfair Display', Georgia, serif",
    fontSize: '34pt',
    fontWeight: 700,
    color: C.navy,
    lineHeight: 1.08,
    letterSpacing: '-0.02em',
    margin: 0,
    ...style,
  }}>{children}</h1>
);

const H2 = ({ children, style }) => (
  <h2 style={{
    fontFamily: "'Playfair Display', Georgia, serif",
    fontSize: '22pt',
    fontWeight: 700,
    color: C.navy,
    lineHeight: 1.15,
    letterSpacing: '-0.015em',
    margin: '0 0 6mm',
    ...style,
  }}>{children}</h2>
);

const Em = ({ children }) => (
  <em style={{ fontStyle: 'italic', color: C.magenta, fontWeight: 600 }}>{children}</em>
);

const Body = ({ children, style }) => (
  <p style={{ fontSize: '11pt', lineHeight: 1.65, color: C.slate, margin: '0 0 5mm', ...style }}>{children}</p>
);

const SectionLabel = ({ children }) => (
  <div style={{
    fontSize: '9pt',
    letterSpacing: '0.24em',
    textTransform: 'uppercase',
    fontWeight: 700,
    color: C.magenta,
    marginBottom: '5mm',
  }}>{children}</div>
);

// ==================== PAGE 1: The Belief ====================

const CoverPage = () => (
  <Page pageNum={1} bg={C.navy} noPad>
    <div style={{ padding: '14mm 16mm', height: '100%', display: 'flex', flexDirection: 'column', color: '#fff', position: 'relative' }}>
      <PageHeader pageNum={1} dark />
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', paddingTop: '20mm' }}>
        <div style={{ marginBottom: '8mm' }}>
          <KeystoneLogo variant="dark" size={40} />
        </div>
        <div style={{
          fontSize: '9.5pt',
          letterSpacing: '0.28em',
          textTransform: 'uppercase',
          color: C.gold,
          fontWeight: 700,
          marginBottom: '8mm',
        }}>
          A conversation, not a pitch
        </div>
        <H1 style={{ color: '#fff', fontSize: '42pt', maxWidth: '170mm' }}>
          Your factory already works.<br/>
          <span style={{ fontStyle: 'italic', color: C.gold, fontWeight: 500 }}>Keystone makes it traceable.</span>
        </H1>
        <div style={{
          marginTop: '10mm',
          fontSize: '13pt',
          color: 'rgba(255,255,255,0.88)',
          lineHeight: 1.55,
          maxWidth: '150mm',
          fontWeight: 400,
        }}>
          The operational middle between the factory floor and accounts. Production, dispatch and traceability for growing food manufacturers.
        </div>

        <div style={{ marginTop: '18mm', padding: '5mm 6mm', background: 'rgba(255,255,255,0.06)', borderLeft: `3px solid ${C.gold}`, maxWidth: '160mm' }}>
          <div style={{ fontSize: '9pt', letterSpacing: '0.2em', textTransform: 'uppercase', color: C.gold, fontWeight: 700, marginBottom: '2mm' }}>
            Built inside a working food factory
          </div>
          <div style={{ fontSize: '11pt', lineHeight: 1.6, color: 'rgba(255,255,255,0.92)' }}>
            Not designed in a Dublin office and pitched afterwards. Written on the production floor of Cashel Farmhouse Cheesemakers in Fethard, Co. Tipperary, against a real Tuesday-morning order deadline. Then generalised for growing food manufacturers across Europe.
          </div>
        </div>
      </div>

      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '6mm',
        marginBottom: '10mm',
        fontSize: '10pt',
        color: 'rgba(255,255,255,0.75)',
      }}>
        <div>
          <div style={{ fontSize: '8pt', letterSpacing: '0.16em', textTransform: 'uppercase', color: C.gold, fontWeight: 700, marginBottom: 3 }}>Live in</div>
          <div style={{ color: '#fff', fontWeight: 600, fontSize: '11pt' }}>Weeks, not quarters</div>
        </div>
        <div>
          <div style={{ fontSize: '8pt', letterSpacing: '0.16em', textTransform: 'uppercase', color: C.gold, fontWeight: 700, marginBottom: 3 }}>Start with</div>
          <div style={{ color: '#fff', fontWeight: 600, fontSize: '11pt' }}>A 20-minute operational fit review</div>
        </div>
      </div>
      <PageFooter dark />
    </div>
  </Page>
);

// ==================== PAGE 2: Their Reality ====================

const RealityPage = () => (
  <Page pageNum={2} bg={C.cream}>
    <PageHeader pageNum={2} />
    <div style={{ paddingTop: '18mm' }}>
      <SectionLabel>02 · Sound familiar?</SectionLabel>
      <H2>
        Running a small food business is a paper factory<br/>
        <Em>bolted onto a food factory.</Em>
      </H2>

      <Body>
        The accounts package handles the invoice. The factory handles the making. Everything in between — order to production plan, production plan to batch, batch to dispatch note, dispatch to trace, trace to auditor — runs on Excel, WhatsApp, sticky notes and one person&rsquo;s memory.
      </Body>

      <div style={{ marginTop: '6mm', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4mm 6mm' }}>
        {[
          ['FRIDAY, 15:47', 'A retailer emails asking which batches went out last Wednesday, formatted their way, before Monday. The spreadsheet that knows is the one that\u2019s a bit broken.'],
          ['SUNDAY, 21:30', 'Someone opens the laptop to reconstruct next week\u2019s production plan from the order emails, because the plan was in her head.'],
          ['THE QUIET FEAR', '"What happens when she\u2019s on holiday?" stops being a rhetorical question.'],
          ['AUDIT DAY', 'The auditor asks for the trace on a single batch. Everyone stops what they\u2019re doing for two hours.'],
        ].map(([t, b], i) => (
          <div key={i} style={{ padding: '4.5mm 5mm', background: C.paper, border: `1px solid ${C.border}`, borderLeft: `3px solid ${C.magenta}` }}>
            <div style={{ fontSize: '8pt', letterSpacing: '0.2em', textTransform: 'uppercase', fontWeight: 700, color: C.magenta, marginBottom: 3 }}>{t}</div>
            <div style={{ fontSize: '10.5pt', color: C.slate, lineHeight: 1.55 }} dangerouslySetInnerHTML={{ __html: b }} />
          </div>
        ))}
      </div>

      <div style={{ marginTop: '8mm', padding: '5mm 6mm', background: C.navy, color: '#fff' }}>
        <div style={{ fontSize: '9pt', letterSpacing: '0.22em', textTransform: 'uppercase', color: C.gold, fontWeight: 700, marginBottom: '3mm' }}>
          The real question
        </div>
        <div style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: '18pt', fontWeight: 700, lineHeight: 1.25, color: '#fff', letterSpacing: '-0.01em' }}>
          Should the people who make our food<br/>
          <span style={{ fontStyle: 'italic', color: C.gold, fontWeight: 500 }}>spend their time making it, or reconstructing what happened afterwards?</span>
        </div>
        <div style={{ marginTop: '4mm', fontSize: '10pt', lineHeight: 1.6, color: 'rgba(255,255,255,0.82)' }}>
          Keystone exists so growing food manufacturers can prove every batch, every dispatch and every decision without turning the factory into an IT project.
        </div>
      </div>
    </div>
    <PageFooter />
  </Page>
);

// ==================== PAGE 3: The Answer (spine) ====================

const SpinePage = () => (
  <Page pageNum={3} bg={C.paper}>
    <PageHeader pageNum={3} />
    <div style={{ paddingTop: '18mm' }}>
      <SectionLabel>03 · The answer</SectionLabel>
      <H2>
        One spine. Four stages.<br/>
        <Em>The way your factory already works.</Em>
      </H2>

      <Body>
        Keystone doesn&rsquo;t arrive with a new operating model. It sits along the operational spine every food manufacturer already has, and makes each stage answer for itself.
      </Body>

      <div style={{ marginTop: '8mm', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '5mm 6mm' }}>
        {[
          {
            n: '01',
            t: 'Inwards',
            sub: 'Goods-in, supplier lots, QC',
            b: 'Every ingredient, packaging item and raw material logged with its lot code the moment it arrives. QC checks captured, not remembered. Later, when a supplier issues a lot recall, you already know which finished batches used it.',
          },
          {
            n: '02',
            t: 'Production',
            sub: 'Make-days, batches, recipes',
            b: 'Batches created from real make-days, with actual weights, actual yields and the operator who ran the shift. Not a plan pretending to be reality.',
          },
          {
            n: '03',
            t: 'Outwards',
            sub: 'Orders, dispatches, retailer docs',
            b: 'Sales orders, dispatch notes and pallet labels formatted the way each retailer expects. Your internal SKU code and the retailer\u2019s code side by side, translated once. Catch-weight kilos on the line, not stapled to the back.',
          },
          {
            n: '04',
            t: 'Traceability',
            sub: 'Batch, recall, audit',
            b: 'From any batch, forward to every customer, or from any customer complaint, backward to the milk that made the cheese. In seconds, as a signed PDF. Corrections preserved, not overwritten.',
          },
        ].map((s) => (
          <div key={s.n} style={{ padding: '5.5mm 6mm', background: C.cream, border: `1px solid ${C.border}`, borderTop: `3px solid ${C.magenta}` }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: '3mm' }}>
              <span style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: '22pt', fontWeight: 700, color: C.magenta, lineHeight: 1 }}>{s.n}</span>
              <div>
                <div style={{ fontSize: '14pt', fontWeight: 700, color: C.navy, letterSpacing: '-0.01em' }}>{s.t}</div>
                <div style={{ fontSize: '9pt', color: C.muted, letterSpacing: '0.06em', marginTop: 1 }}>{s.sub}</div>
              </div>
            </div>
            <div style={{ fontSize: '10pt', color: C.slate, lineHeight: 1.6 }}>{s.b}</div>
          </div>
        ))}
      </div>

      <div style={{ marginTop: '6mm', padding: '4mm 5mm', background: C.gold + '20', border: `1px solid ${C.gold}66` }}>
        <div style={{ fontSize: '10pt', color: C.navy, lineHeight: 1.55 }}>
          <strong>The category we occupy.</strong> Accounts software already exists. Factories already work. Keystone is the operational middle, between what your accounts package can see and what your production floor knows.
        </div>
      </div>
    </div>
    <PageFooter />
  </Page>
);

// ==================== PAGE 4: Proof ====================

const ProofPage = () => (
  <Page pageNum={4} bg={C.cream}>
    <PageHeader pageNum={4} />
    <div style={{ paddingTop: '18mm' }}>
      <SectionLabel>04 · Proof</SectionLabel>
      <H2>
        Built with a manufacturer,<br/>
        <Em>not demonstrated to one.</Em>
      </H2>

      <Body>
        Keystone&rsquo;s founding production deployment is at Cashel Farmhouse Cheesemakers, an internationally recognised Irish blue-cheese producer in Fethard, Co. Tipperary. We didn&rsquo;t ask them what software they wanted. We watched how they worked, then built around it.
      </Body>

      <div style={{ marginTop: '7mm', padding: '6mm 7mm', background: C.paper, border: `1px solid ${C.border}`, borderLeft: `4px solid ${C.gold}` }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '5mm' }}>
          {[
            ['40+', 'Internal SKUs'],
            ['9', 'Export markets'],
            ['Retail · Foodservice', 'Channels'],
            ['Private label + own brand', 'Business model'],
          ].map(([v, k]) => (
            <div key={k}>
              <div style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: '20pt', fontWeight: 700, color: C.magenta, lineHeight: 1, letterSpacing: '-0.02em' }}>{v}</div>
              <div style={{ fontSize: '8.5pt', color: C.slate, marginTop: 4, letterSpacing: '0.05em' }}>{k}</div>
            </div>
          ))}
        </div>
      </div>

      <div style={{ marginTop: '7mm', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '5mm' }}>
        {[
          {
            t: 'The week we watched',
            b: 'Orders arriving from Tesco, Musgrave, foodservice distributors and export markets. Cheese being made. Retailer codes being translated. COAs being prepared. Dispatches leaving. Auditors asking questions. We built Keystone around that week.',
          },
          {
            t: 'What it replaced',
            b: 'A 28-tab Excel workbook, a WhatsApp group and one person\u2019s memory. Nothing was wrong with the operation, everything was wrong with how much of it lived in her head.',
          },
        ].map((s, i) => (
          <div key={i} style={{ padding: '5mm 5.5mm', background: C.paper, border: `1px solid ${C.border}`, borderTop: `3px solid ${C.magenta}` }}>
            <div style={{ fontSize: '11pt', fontWeight: 700, color: C.navy, marginBottom: 4 }}>{s.t}</div>
            <div style={{ fontSize: '10pt', color: C.slate, lineHeight: 1.55 }}>{s.b}</div>
          </div>
        ))}
      </div>

      <div style={{ marginTop: '7mm', padding: '5mm 6mm', background: C.navy, color: '#fff' }}>
        <div style={{ fontSize: '9pt', letterSpacing: '0.22em', textTransform: 'uppercase', color: C.gold, fontWeight: 700, marginBottom: '2.5mm' }}>
          Don&rsquo;t take our word for it
        </div>
        <div style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: '17pt', fontWeight: 700, lineHeight: 1.25, color: '#fff', letterSpacing: '-0.01em' }}>
          Speak to the people using it.
        </div>
        <div style={{ marginTop: '3mm', fontSize: '10.5pt', lineHeight: 1.6, color: 'rgba(255,255,255,0.82)' }}>
          After a short discovery call, we&rsquo;ll broker a 20-minute reference conversation with Cashel Blue&rsquo;s QA and Operations team. They&rsquo;ll tell you, in their own words, what works, what doesn&rsquo;t, and how the rollout actually went.
        </div>
      </div>

      <div style={{ marginTop: '5mm', fontSize: '8.5pt', color: C.muted, fontStyle: 'italic', lineHeight: 1.5 }}>
        Cashel Blue<sup>®</sup> and Crozier Blue<sup>®</sup> are registered trademarks of J&amp;L Grubb Ltd.
      </div>
    </div>
    <PageFooter />
  </Page>
);

// ==================== PAGE 5: The Conversation ====================

const ConversationPage = () => (
  <Page pageNum={5} bg={C.navy} noPad last>
    <div style={{ padding: '14mm 16mm', height: '100%', display: 'flex', flexDirection: 'column', color: '#fff', position: 'relative' }}>
      <PageHeader pageNum={5} dark />
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', paddingTop: '20mm' }}>
        <div style={{ fontSize: '9.5pt', letterSpacing: '0.26em', textTransform: 'uppercase', color: C.gold, fontWeight: 700, marginBottom: '6mm' }}>
          05 · The conversation
        </div>
        <H1 style={{ color: '#fff', fontSize: '46pt', letterSpacing: '-0.02em' }}>
          Bring us your<br/>
          <span style={{ fontStyle: 'italic', color: C.gold, fontWeight: 500 }}>worst Friday.</span>
        </H1>
        <div style={{ marginTop: '9mm', fontSize: '13pt', color: 'rgba(255,255,255,0.9)', lineHeight: 1.6, maxWidth: '160mm' }}>
          Give us twenty minutes. Tell us how orders arrive, how production gets planned, how dispatch works and what happens when somebody asks for a batch trace. We&rsquo;ll tell you whether Keystone fits. If it doesn&rsquo;t, we&rsquo;ll say so.
        </div>

        <div style={{ marginTop: '10mm', padding: '5.5mm 6mm', background: 'rgba(255,255,255,0.06)', borderLeft: `3px solid ${C.gold}`, maxWidth: '170mm' }}>
          <div style={{ fontSize: '9pt', letterSpacing: '0.22em', textTransform: 'uppercase', color: C.gold, fontWeight: 700, marginBottom: '3mm' }}>
            20-minute operational fit review
          </div>
          <div style={{ fontSize: '11pt', color: 'rgba(255,255,255,0.9)', lineHeight: 1.6 }}>
            Not a demo. Not a sales pitch. A working conversation about your operation with the person who will implement the system. Written scope within 48 hours if we fit, referral to someone better if we don&rsquo;t.
          </div>
        </div>

        <div style={{ marginTop: '10mm', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '5mm', maxWidth: '170mm' }}>
          <div style={{ padding: '5mm 5.5mm', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.12)' }}>
            <div style={{ fontSize: '8pt', letterSpacing: '0.18em', textTransform: 'uppercase', color: C.gold, fontWeight: 700, marginBottom: 4 }}>Book online</div>
            <div style={{ fontSize: '11pt', fontWeight: 700, color: '#fff' }}>keystone.cloudvoro.com/site/contact</div>
            <div style={{ fontSize: '9.5pt', color: 'rgba(255,255,255,0.7)', marginTop: 2 }}>Pick a 20-minute slot. Video call.</div>
          </div>
          <div style={{ padding: '5mm 5.5mm', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.12)' }}>
            <div style={{ fontSize: '8pt', letterSpacing: '0.18em', textTransform: 'uppercase', color: C.gold, fontWeight: 700, marginBottom: 4 }}>Reply directly</div>
            <div style={{ fontSize: '11pt', fontWeight: 700, color: '#fff' }}>asif@cloudvoro.com</div>
            <div style={{ fontSize: '9.5pt', color: 'rgba(255,255,255,0.7)', marginTop: 2 }}>Founder-monitored. Reply within one working day.</div>
          </div>
        </div>

        <div style={{ marginTop: '10mm', padding: '4.5mm 5.5mm', background: 'rgba(201,169,97,0.10)', borderLeft: `2px solid ${C.gold}`, maxWidth: '170mm' }}>
          <div style={{ fontSize: '9pt', color: 'rgba(255,255,255,0.85)', lineHeight: 1.6 }}>
            <strong style={{ color: C.gold }}>Prefer the detail first?</strong> The 16-page buyer pack covering intake, production, dispatch, traceability, onboarding, security and fit is at <span style={{ color: '#fff', fontWeight: 700 }}>keystone.cloudvoro.com/brochure/buyer-pack</span>.
          </div>
        </div>
      </div>

      <div style={{ borderTop: '1px solid rgba(255,255,255,0.15)', paddingTop: '5mm', marginBottom: '10mm', display: 'flex', justifyContent: 'space-between', alignItems: 'end' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <KeystoneLogo variant="dark" size={24} />
          <div>
            <div style={{ fontSize: '9pt', color: 'rgba(255,255,255,0.75)' }}>Cloudvoro Ltd &middot; Irish operations</div>
            <div style={{ fontSize: '8pt', color: 'rgba(255,255,255,0.55)', marginTop: 1 }}>1 Emmett St, Thurles, Co. Tipperary, E41 YH05, Ireland &middot; asif@cloudvoro.com</div>
            <div style={{ fontSize: '7.5pt', color: 'rgba(255,255,255,0.4)', marginTop: 1 }}>CRO 795787 &middot; VAT IE4490902SH</div>
          </div>
        </div>
        <div style={{ fontSize: '9pt', color: C.gold, fontStyle: 'italic', letterSpacing: '0.04em' }}>
          Make the food. Keystone remembers the rest.
        </div>
      </div>
      <PageFooter dark />
    </div>
  </Page>
);

// ==================== Root ====================

function BrochureSales() {
  const [busy, setBusy] = React.useState(false);
  const download = async () => {
    setBusy(true);
    try { await downloadServerPDF('/brochure/sales', 'Keystone-Conversation-Brochure.pdf'); }
    catch (e) { alert(e?.message || 'PDF render failed'); }
    setBusy(false);
  };

  return (
    <div style={{ background: '#F1F5F9', minHeight: '100vh', padding: '24px 0' }} data-testid="brochure-sales-conversation">
      {/* Screen-only sticky download bar */}
      <div style={{
        position: 'sticky', top: 12, zIndex: 10, maxWidth: '210mm', margin: '0 auto 20px',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        background: '#fff', border: `1px solid ${C.border}`, borderRadius: 8,
        padding: '10px 16px', boxShadow: '0 4px 16px rgba(10,37,64,0.08)',
      }} className="brochure-actions no-print">
        <div style={{ fontSize: 13, color: C.slate }}>
          <strong style={{ color: C.navy }}>5-page conversation brochure.</strong> A shorter read designed to start a 20-minute chat, not close a sale.
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <a
            href="/brochure/buyer-pack"
            data-testid="brochure-sales-buyer-pack-link"
            style={{
              padding: '10px 18px', background: '#fff', color: C.navy,
              border: `1px solid ${C.border}`, borderRadius: 4, fontWeight: 700,
              fontSize: 12, letterSpacing: '0.06em', textTransform: 'uppercase',
              textDecoration: 'none',
            }}
          >
            16-page buyer pack →
          </a>
          <button
            onClick={download}
            disabled={busy}
            data-testid="brochure-sales-download-pdf"
            style={{
              padding: '10px 18px', background: C.magenta, color: '#fff', border: 'none',
              borderRadius: 4, fontWeight: 700, fontSize: 12, letterSpacing: '0.06em',
              textTransform: 'uppercase', cursor: busy ? 'wait' : 'pointer',
              opacity: busy ? 0.7 : 1,
            }}
          >
            {busy ? 'Rendering PDF\u2026' : 'Download PDF'}
          </button>
        </div>
      </div>

      <CoverPage />
      <RealityPage />
      <SpinePage />
      <ProofPage />
      <ConversationPage />

      <style>{`
        @media print {
          html, body { background: #fff !important; margin: 0 !important; padding: 0 !important; }
          #root, #root > * { background: #fff !important; margin: 0 !important; padding: 0 !important; }
          .no-print { display: none !important; }
          .brochure-page {
            margin: 0 !important;
            padding: 14mm 16mm !important;
            box-shadow: none !important;
            border: 0 !important;
            width: 210mm !important;
            height: 297mm !important;
            overflow: hidden !important;
            page-break-after: always;
            break-after: page;
            page-break-inside: avoid;
            break-inside: avoid;
          }
          .brochure-page:last-of-type {
            page-break-after: auto;
            break-after: auto;
          }
        }
        @page { size: A4; margin: 0; }
      `}</style>
    </div>
  );
}

export default BrochureSales;
